/**
 * `FlashSession` — orchestrates the 5-stage flash pipeline that
 * mirrors WinKFP's `coapiKfProgSgD2`.
 *
 *   RESOLVE → PRECHECK → BACKUP → PROGRAM → POSTCHECK
 *
 * See `types.ts` for the per-stage semantics. The PROGRAM stage is
 * a single `SG_PROGRAMMIEREN` IPO dispatch — all the wire-level UDS
 * handshaking lives inside the IPO, not here.
 */
import { parseS37, buildMemoryMap, parsePaDa, paDaToRegions, } from '@emdzej/nfsx-flash-data';
import { runPrecheck } from './precheck.js';
import { runBackup, emitBackup } from './backup.js';
import { runProgramSg } from './prog-sg.js';
import { buildRegionsFirmwareSource, buildPaDaRecordSource, } from './firmware-source.js';
import { rejectAllConfirmation } from './safety.js';
export class FlashSession {
    opts;
    listeners = [];
    events = [];
    /**
     * Parsed `.0PA` records, populated when the firmware source is a
     * PA/DA file. Carries the Intel-HEX framing the SGBD requires —
     * regions alone don't preserve per-record framing. See
     * `firmware-source.ts` / `docs/architecture.md` §11.13.
     */
    paDaRecords;
    constructor(opts) {
        this.opts = opts;
    }
    /** Subscribe to per-event updates. Returns an unsubscribe function. */
    on(_kind, cb) {
        this.listeners.push(cb);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== cb);
        };
    }
    /**
     * Run the pipeline. Defaults to `dryRun: true` and
     * `confirm: rejectAllConfirmation` — callers must explicitly opt
     * into both `dryRun: false` and a real confirmation function for
     * the PROGRAM stage to execute.
     */
    async run(runOpts = {}) {
        const dryRun = runOpts.dryRun ?? true;
        const skipBackup = runOpts.skipBackup ?? false;
        const skipPostcheck = runOpts.skipPostcheck ?? false;
        const confirm = runOpts.confirm ?? rejectAllConfirmation;
        let totalBytes = 0;
        let backupPath;
        const stagesRun = [];
        let programDiagnostics;
        // ── RESOLVE ───────────────────────────────────────────────────
        // Parse the firmware file for integrity. Doesn't pass bytes to
        // the IPO — the IPO reads from disk via CABF* syscalls.
        let regions;
        {
            const t0 = Date.now();
            this.emit({ type: 'stage:start', stage: 'RESOLVE' });
            try {
                regions = this.resolveFirmware();
                totalBytes = regions.reduce((n, r) => n + r.bytes.length, 0);
                stagesRun.push('RESOLVE');
                this.emit({ type: 'stage:done', stage: 'RESOLVE', durationMs: Date.now() - t0 });
                this.emit({
                    type: 'log',
                    level: 'info',
                    message: `firmware: ${regions.length} region(s), ${totalBytes} bytes total`,
                });
            }
            catch (err) {
                return this.abortAt('RESOLVE', err, stagesRun, totalBytes, backupPath, dryRun);
            }
        }
        // ── PRECHECK ──────────────────────────────────────────────────
        {
            const t0 = Date.now();
            this.emit({ type: 'stage:start', stage: 'PRECHECK' });
            try {
                const report = await runPrecheck(this.opts.ecu, this.opts.ediabas, this.opts.startRuntime, this.opts.precheck);
                stagesRun.push('PRECHECK');
                this.emitPrecheckSummary(report);
                if (!report.ok) {
                    const failed = [];
                    if (!report.hwReferenz.ok && !report.hwReferenz.skipped)
                        failed.push(`hw_referenz (${report.hwReferenz.reason})`);
                    if (!report.sgStatus.ok && !report.sgStatus.skipped)
                        failed.push(`sg_status (${report.sgStatus.reason})`);
                    if (!report.sgIdent.ok && !report.sgIdent.skipped)
                        failed.push(`sg_ident (${report.sgIdent.reason})`);
                    if (!report.sgAif.ok && !report.sgAif.skipped)
                        failed.push(`sg_aif (${report.sgAif.reason})`);
                    if (!report.hwnrMatch.ok && !report.hwnrMatch.skipped)
                        failed.push(`hwnr_match (${report.hwnrMatch.reason})`);
                    if (!report.fsc.ok && !report.fsc.skipped)
                        failed.push(`fsc (${report.fsc.reason})`);
                    return this.abortAt('PRECHECK', `precheck failed: ${failed.join('; ')}`, stagesRun, totalBytes, backupPath, dryRun);
                }
                this.emit({ type: 'stage:done', stage: 'PRECHECK', durationMs: Date.now() - t0 });
            }
            catch (err) {
                return this.abortAt('PRECHECK', err, stagesRun, totalBytes, backupPath, dryRun);
            }
        }
        // ── BACKUP ────────────────────────────────────────────────────
        // Audit snapshot. Read-only, always safe to run. Skippable via
        // `backup: { skip: true }` for tests / CI / explicit operator opt-out.
        {
            const t0 = Date.now();
            this.emit({ type: 'stage:start', stage: 'BACKUP' });
            try {
                if (skipBackup || this.opts.backup?.skip) {
                    this.emit({
                        type: 'stage:skipped',
                        stage: 'BACKUP',
                        reason: skipBackup ? '--no-backup' : 'backup.skip = true',
                    });
                }
                else {
                    const report = await runBackup(this.opts.ecu, this.opts.ediabas, this.opts.startRuntime);
                    const emitter = this.opts.backup?.emitter;
                    if (emitter) {
                        backupPath = await emitBackup(report, emitter, this.opts.backup?.filename);
                        if (backupPath) {
                            this.emit({ type: 'log', level: 'info', message: `backup saved → ${backupPath}` });
                        }
                        else {
                            this.emit({ type: 'log', level: 'info', message: `backup emitted` });
                        }
                    }
                    else {
                        this.emit({
                            type: 'log',
                            level: 'info',
                            message: `backup captured (no emitter — audit trace only)`,
                        });
                    }
                }
                stagesRun.push('BACKUP');
                this.emit({ type: 'stage:done', stage: 'BACKUP', durationMs: Date.now() - t0 });
            }
            catch (err) {
                return this.abortAt('BACKUP', err, stagesRun, totalBytes, backupPath, dryRun);
            }
        }
        // ── PROGRAM ───────────────────────────────────────────────────
        // The only destructive stage. Skipped in dry-run; gated by
        // confirm() otherwise.
        {
            if (dryRun) {
                this.emit({ type: 'stage:skipped', stage: 'PROGRAM', reason: 'dry-run' });
                stagesRun.push('PROGRAM');
            }
            else {
                const ctx = {
                    ecu: this.opts.ecu,
                    totalBytes,
                    regionCount: regions.length,
                };
                const proceed = await Promise.resolve(confirm('PROGRAM', ctx));
                if (!proceed) {
                    return this.abortAt('PROGRAM', 'operator declined confirmation', stagesRun, totalBytes, backupPath, dryRun);
                }
                const t0 = Date.now();
                this.emit({ type: 'stage:start', stage: 'PROGRAM' });
                try {
                    // The IPO pops one record per call from this iterator via
                    // slot 0x55. PA/DA inputs use the BMW-framed source (one
                    // Intel-HEX record per chunk, with the 22-byte header the
                    // SGBD's `pary` opcode parses). S37 / raw regions fall back
                    // to the older unframed splitter — kept for tests + non-BMW
                    // payloads. See `docs/architecture.md` §11.13.
                    const firmwareSource = this.paDaRecords
                        ? buildPaDaRecordSource(this.paDaRecords)
                        : buildRegionsFirmwareSource(regions);
                    const totalChunks = this.paDaRecords
                        ? this.paDaRecords.filter((r) => r.type === 0x00 && r.data.length > 0).length
                        : regions.reduce((n, r) => n + Math.ceil(r.bytes.length / 246), 0);
                    const onProgress = this.makeProgressEmitter(totalChunks, totalBytes);
                    const report = await runProgramSg(this.opts.ecu, this.opts.ediabas, this.opts.startRuntime, this.opts.program, firmwareSource, onProgress);
                    programDiagnostics = {
                        firmwareStats: report.firmwareStats,
                        ediabasJobs: report.ediabasJobs,
                        slotTrace: report.slotTrace,
                    };
                    this.emitProgramDiagnostics(report);
                    if (!report.ok) {
                        return this.abortAt('PROGRAM', report.reason ?? 'SG_PROGRAMMIEREN failed', stagesRun, totalBytes, backupPath, dryRun, programDiagnostics);
                    }
                    stagesRun.push('PROGRAM');
                    this.emit({ type: 'stage:done', stage: 'PROGRAM', durationMs: Date.now() - t0 });
                }
                catch (err) {
                    return this.abortAt('PROGRAM', err, stagesRun, totalBytes, backupPath, dryRun);
                }
            }
        }
        // ── POSTCHECK ─────────────────────────────────────────────────
        // Re-run identity reads to confirm the ECU still responds. Skipped
        // in dry-run (nothing changed, so no need to verify) and when the
        // operator passed `--no-verify`. Fail-soft: errors here are
        // diagnostic, not flash failures.
        if (skipPostcheck) {
            this.emit({ type: 'stage:skipped', stage: 'POSTCHECK', reason: '--no-verify' });
            stagesRun.push('POSTCHECK');
        }
        else {
            const t0 = Date.now();
            this.emit({ type: 'stage:start', stage: 'POSTCHECK' });
            try {
                if (!dryRun) {
                    const report = await runPrecheck(this.opts.ecu, this.opts.ediabas, this.opts.startRuntime, 
                    // Skip FSC + hwnr_match — after a successful flash the
                    // identity may have changed; we just want a liveness check.
                    { skip: ['fsc', 'hwnr_match', 'sg_aif'] });
                    if (!report.hwReferenz.ok) {
                        this.emit({
                            type: 'log',
                            level: 'warn',
                            message: `POSTCHECK: HW_REFERENZ failed (${report.hwReferenz.reason}) — ECU may not have reset cleanly`,
                        });
                    }
                    else if (report.hwReferenz.kennung) {
                        this.emit({
                            type: 'log',
                            level: 'info',
                            message: `POSTCHECK: HW_REFERENZ kennung=${report.hwReferenz.kennung} projekt=${report.hwReferenz.projekt ?? '?'}`,
                        });
                    }
                }
                stagesRun.push('POSTCHECK');
                this.emit({ type: 'stage:done', stage: 'POSTCHECK', durationMs: Date.now() - t0 });
            }
            catch (err) {
                // Fail-soft: log but don't abort.
                this.emit({
                    type: 'log',
                    level: 'warn',
                    message: `POSTCHECK threw: ${err instanceof Error ? err.message : String(err)}`,
                });
                stagesRun.push('POSTCHECK');
                this.emit({ type: 'stage:done', stage: 'POSTCHECK', durationMs: Date.now() - t0 });
            }
        }
        const result = {
            ok: true,
            stagesRun,
            totalBytes,
            dryRun,
            events: [...this.events],
        };
        if (backupPath)
            result.backupPath = backupPath;
        if (programDiagnostics)
            result.programDiagnostics = programDiagnostics;
        return result;
    }
    // ── Firmware parsing helpers ────────────────────────────────────
    resolveFirmware() {
        const f = this.opts.firmware;
        if ('regions' in f)
            return [...f.regions];
        if ('paDaBytes' in f)
            return this.resolvePaDa(f.paDaBytes);
        return this.resolveS37(f.s37Bytes);
    }
    resolveS37(bytes) {
        const { records, skipped } = parseS37(bytes);
        if (skipped.length > 0) {
            this.emit({
                type: 'log',
                level: 'warn',
                message: `S37 parse: skipped ${skipped.length} lines (first: line ${skipped[0].lineNumber} ${skipped[0].reason})`,
            });
        }
        const map = buildMemoryMap(records);
        if (map.overlaps.length > 0) {
            throw new Error(`firmware has ${map.overlaps.length} overlapping memory range(s) — refusing to flash`);
        }
        return map.regions;
    }
    resolvePaDa(bytes) {
        const result = parsePaDa(bytes);
        // Stash records so PROGRAM can frame them via the BMW layout —
        // memory regions alone strip the per-record framing the SGBD's
        // pary opcode requires.
        this.paDaRecords = result.records;
        if (result.skipped.length > 0) {
            this.emit({
                type: 'log',
                level: 'warn',
                message: `PA/DA parse: skipped ${result.skipped.length} lines (first: line ${result.skipped[0].lineNumber} ${result.skipped[0].reason})`,
            });
        }
        const bad = result.records.filter((r) => !r.checksumOk);
        if (bad.length > 0) {
            throw new Error(`PA/DA file has ${bad.length} record(s) with bad checksums — refusing to flash`);
        }
        const regions = paDaToRegions(result);
        if (regions.length === 0) {
            throw new Error('PA/DA file parsed but contained no data records');
        }
        if (result.metadata.referenz) {
            this.emit({
                type: 'log',
                level: 'info',
                message: `firmware: $REFERENZ ${result.metadata.referenz.ref} ${result.metadata.referenz.flag}`,
            });
        }
        if (result.metadata.checksum) {
            this.emit({
                type: 'log',
                level: 'info',
                message: `firmware: $CHECKSUMME ${result.metadata.checksum.value} ${result.metadata.checksum.flag}`,
            });
        }
        return regions;
    }
    // ── Event helpers ───────────────────────────────────────────────
    emit(e) {
        this.events.push(e);
        for (const l of this.listeners)
            l(e);
    }
    /**
     * Build a throttled progress emitter the firmware-source can call
     * after every chunk it delivers. Logs at most every ~2 seconds OR
     * every 5% of chunks (whichever comes first), so the operator sees
     * steady output during the multi-minute PROGRAM stage without
     * spamming the console.
     */
    makeProgressEmitter(totalChunks, totalBytes) {
        const t0 = Date.now();
        let lastLogTime = t0;
        let lastLogCalls = 0;
        const chunkStep = Math.max(1, Math.floor(totalChunks / 20)); // 5%
        return (stats) => {
            const now = Date.now();
            const elapsed = now - t0;
            const sinceLastLog = now - lastLogTime;
            const sinceLastChunk = stats.calls - lastLogCalls;
            // Throttle: log when EITHER 2s have passed OR we've crossed the
            // next 5% bucket. Always log the very first chunk so the
            // operator knows the wire is alive.
            if (stats.calls === 1 || sinceLastChunk >= chunkStep || sinceLastLog >= 2000) {
                lastLogTime = now;
                lastLogCalls = stats.calls;
                const pct = totalChunks > 0 ? (stats.calls / totalChunks) * 100 : 0;
                const kbDone = (stats.bytesDelivered / 1024).toFixed(1);
                const kbTotal = (totalBytes / 1024).toFixed(1);
                const rateKbPerSec = elapsed > 0 ? (stats.bytesDelivered / 1024 / (elapsed / 1000)) : 0;
                const remainingChunks = Math.max(0, totalChunks - stats.calls);
                const etaSec = rateKbPerSec > 0 && stats.bytesDelivered > 0
                    ? Math.round((remainingChunks * (elapsed / Math.max(1, stats.calls))) / 1000)
                    : -1;
                const etaStr = etaSec >= 0 ? `ETA ${formatDuration(etaSec)}` : 'ETA —';
                this.emit({
                    type: 'log',
                    level: 'info',
                    message: `PROGRAM: chunk ${stats.calls}/${totalChunks} (${pct.toFixed(1)}%)  ${kbDone}/${kbTotal} KB  ${rateKbPerSec.toFixed(1)} KB/s  ${etaStr}`,
                });
            }
        };
    }
    emitProgramDiagnostics(r) {
        if (r.firmwareStats) {
            const s = r.firmwareStats;
            this.emit({
                type: 'log',
                level: s.bytesDelivered > 0 ? 'info' : 'warn',
                message: `firmware iterator: ${s.calls} calls, ${s.bytesDelivered} bytes delivered, drained=${s.drained}`,
            });
            if (s.calls === 0 || s.bytesDelivered === 0) {
                this.emit({
                    type: 'log',
                    level: 'warn',
                    message: `⚠ IPO never drained the firmware source — the flash loop body did not execute. NO BYTES WERE WRITTEN.`,
                });
            }
        }
        else {
            this.emit({
                type: 'log',
                level: 'warn',
                message: `firmware iterator: not instrumented (firmwareSource missing)`,
            });
        }
        const j = r.ediabasJobs;
        const totalDispatches = j.byJob.size + j.binByJob.size;
        if (totalDispatches === 0) {
            this.emit({
                type: 'log',
                level: 'warn',
                message: `⚠ ediabas: 0 jobs dispatched during PROGRAM. The IPO did not talk to the ECU.`,
            });
        }
        else {
            // String-path (CDHapiJob) and binary-path (CDHapiJobData) live in
            // separate counters — FLASH_LOESCHEN/SCHREIBEN typically go via
            // the binary path. Show both.
            const stringJobs = [];
            for (const [name, count] of j.byJob)
                stringJobs.push(`${name}×${count}`);
            const binJobs = [];
            for (const [name, count] of j.binByJob)
                binJobs.push(`${name}×${count}`);
            this.emit({
                type: 'log',
                level: 'info',
                message: `ediabas string-path: ${stringJobs.join(', ') || '(none)'} | totalMs=${j.totalMs}`,
            });
            this.emit({
                type: 'log',
                level: 'info',
                message: `ediabas binary-path: ${binJobs.join(', ') || '(none)'} | ${j.binBytesPushed} bytes pushed | totalMs=${j.binTotalMs}`,
            });
            // Retries: count how many slot 0x0E dispatches needed re-tries.
            // Each `:retry` trace entry represents one re-dispatch attempt
            // that the operator should know about — high counts suggest
            // wire issues worth investigating at the hardware level.
            const retryCounts = new Map();
            for (const e of r.slotTrace) {
                if (e.name === 'CDHapiJobData:retry') {
                    const reason = e.args.status ?? e.args.error ?? 'unknown';
                    const key = `${e.args.job}/${reason}`;
                    retryCounts.set(key, (retryCounts.get(key) ?? 0) + 1);
                }
            }
            if (retryCounts.size > 0) {
                const total = Array.from(retryCounts.values()).reduce((a, b) => a + b, 0);
                const parts = Array.from(retryCounts.entries()).map(([k, n]) => `${k}×${n}`);
                this.emit({
                    type: 'log',
                    level: 'info',
                    message: `retries: ${total} total (${parts.join(', ')})`,
                });
            }
            const sawFlashWrite = j.byJob.has('FLASH_SCHREIBEN') || j.binByJob.has('FLASH_SCHREIBEN');
            if (!sawFlashWrite) {
                this.emit({
                    type: 'log',
                    level: 'warn',
                    message: `⚠ FLASH_SCHREIBEN was never dispatched (neither string nor binary path). The IPO didn't write any bytes.`,
                });
            }
        }
    }
    emitPrecheckSummary(r) {
        if (r.hwReferenz.ok && !r.hwReferenz.skipped && r.hwReferenz.kennung) {
            this.emit({
                type: 'log',
                level: 'info',
                message: `HW_REFERENZ: kennung=${r.hwReferenz.kennung} projekt=${r.hwReferenz.projekt ?? '?'}`,
            });
        }
        if (r.sgStatus.ok && !r.sgStatus.skipped) {
            const bits = [];
            if (r.sgStatus.status !== undefined)
                bits.push(`status=0x${r.sgStatus.status.toString(16).padStart(2, '0')}`);
            if (r.sgStatus.progTyp !== undefined)
                bits.push(`prog_typ=${r.sgStatus.progTyp}`);
            if (r.sgStatus.progOrder !== undefined)
                bits.push(`prog_order=${r.sgStatus.progOrder}`);
            if (bits.length > 0)
                this.emit({ type: 'log', level: 'info', message: `SG_STATUS: ${bits.join(' ')}` });
        }
        if (r.sgIdent.ok && !r.sgIdent.skipped && r.sgIdent.bmwNr) {
            this.emit({
                type: 'log',
                level: 'info',
                message: `SG_IDENT: bmwnr=${r.sgIdent.bmwNr} sw=${r.sgIdent.swNr ?? '?'} hw=${r.sgIdent.hwNr ?? '?'} prod=${r.sgIdent.prodNr ?? '?'}`,
            });
        }
        if (r.sgAif.ok && !r.sgAif.skipped && r.sgAif.zbNr) {
            this.emit({
                type: 'log',
                level: 'info',
                message: `SG_AIF: zb=${r.sgAif.zbNr} sw=${r.sgAif.swNr ?? '?'} index=${r.sgAif.aenderungsIndex ?? '?'} datum=${r.sgAif.datum ?? '?'}`,
            });
        }
        if (r.hwnrMatch.ok && !r.hwnrMatch.skipped && r.hwnrMatch.actual && r.hwnrMatch.expected) {
            this.emit({
                type: 'log',
                level: 'info',
                message: `HWNR_MATCH: ${r.hwnrMatch.actual} == ${r.hwnrMatch.expected} ✓`,
            });
        }
    }
    abortAt(stage, reason, stagesRun, totalBytes, backupPath, dryRun, programDiagnostics) {
        const msg = reason instanceof Error ? reason.message : String(reason);
        this.emit({ type: 'log', level: 'error', message: `aborting at ${stage}: ${msg}` });
        const result = {
            ok: false,
            stagesRun,
            abortedAt: stage,
            abortReason: msg,
            totalBytes,
            dryRun,
            events: [...this.events],
        };
        if (backupPath)
            result.backupPath = backupPath;
        if (programDiagnostics)
            result.programDiagnostics = programDiagnostics;
        return result;
    }
}
/** Format a seconds count as `Hh Mm Ss` / `Mm Ss` / `Ss`. */
function formatDuration(totalSec) {
    if (totalSec < 60)
        return `${totalSec}s`;
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    if (m < 60)
        return `${m}m ${s}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m ${s}s`;
}
//# sourceMappingURL=session.js.map